import React, { useState } from 'react';
import { Check, MoreHorizontal, Plus, Tag } from 'lucide-react';
import type { ImageTag, ImageTagColor } from '../../services/api/imageTagService';
import type { BannerTagsController } from '../../pages/ads/components/useBannerTags';
import TagPopover from './TagPopover';
import { MAX_TAG_NAME_LENGTH, TagEditor } from './ImageTagPicker';
import { tagColorClasses } from './imageTagColors';
import { TaskyButton, TaskyIconButton } from '../tasky';

// これより多いときは「ほか n 件」にまとめ、行が縦に伸びすぎないようにする
const VISIBLE_LIMIT = 12;

const TagChip: React.FC<{
  tag: ImageTag;
  selected: boolean;
  onToggle: () => void;
  onUpdate: (changes: { name?: string; color?: ImageTagColor }) => void;
  onDelete: () => void;
}> = ({ tag, selected, onToggle, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const colors = tagColorClasses(tag.color);

  return (
    <span
      className={`inline-flex h-8 items-center rounded-full border text-sm ${
        selected ? `${colors.chip} border-current` : 'border-warm-border bg-white text-gray-700'
      }`}
    >
      <TaskyButton
        type="button"
        variant="ghost"
        size="sm"
        aria-pressed={selected}
        onClick={onToggle}
        className="h-full gap-1.5 rounded-l-full rounded-r-none pl-3 pr-1 font-normal text-current hover:bg-black/5 hover:text-current [&_svg]:size-3.5"
      >
        {selected ? (
          <Check strokeWidth={3} />
        ) : (
          <span className={`h-2 w-2 flex-none rounded-full ${colors.dot}`} aria-hidden />
        )}
        <span className="max-w-[10rem] truncate">{tag.name}</span>
      </TaskyButton>
      <TagPopover
        open={editing}
        onOpenChange={setEditing}
        label={`${tag.name}を編集`}
        renderTrigger={({ ref, props }) => (
          <TaskyIconButton
            ref={ref}
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`${tag.name}を編集`}
            title="名前・色を変える"
            className="mr-1 h-6 w-6 rounded-full text-current opacity-50 hover:bg-black/5 hover:text-current hover:opacity-100 [&_svg]:size-3.5"
            {...props}
          >
            <MoreHorizontal />
          </TaskyIconButton>
        )}
      >
        <TagEditor
          tag={tag}
          onSave={onUpdate}
          onDelete={() => {
            // 確認ダイアログの上にポップアップが重ならないよう先に閉じる
            setEditing(false);
            onDelete();
          }}
          onDone={() => setEditing(false)}
        />
      </TagPopover>
    </span>
  );
};

const NewTagButton: React.FC<{ onCreate: (name: string) => void }> = ({ onCreate }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const submit = () => {
    if (!trimmed) return;
    onCreate(trimmed);
    setName('');
    setOpen(false);
  };

  return (
    <TagPopover
      open={open}
      onOpenChange={setOpen}
      label="新しいタグ"
      renderTrigger={({ ref, props }) => (
        <TaskyButton
          ref={ref}
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 rounded-full border border-dashed border-gray-300 px-3 font-normal text-gray-600 hover:border-primary hover:bg-transparent hover:text-primary"
          {...props}
        >
          <Plus />
          新しいタグ
        </TaskyButton>
      )}
    >
      <div className="flex items-center gap-1.5">
        <input
          aria-label="新しいタグの名前"
          placeholder="タグの名前"
          value={name}
          maxLength={MAX_TAG_NAME_LENGTH}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            // 日本語入力の確定の Enter では作らない
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
          className="min-w-0 flex-1 rounded-md border border-warm-border px-2.5 py-2 text-sm focus:border-primary focus:outline-none"
        />
        <TaskyButton type="button" size="sm" onClick={submit} disabled={!trimmed}>
          作成
        </TaskyButton>
      </div>
    </TagPopover>
  );
};

/** 一覧の上の2段目。組織のタグを並べ、押すと絞り込む。複数選ぶと、全部付いたバナーだけになる */
const ImageTagBar: React.FC<{ controller: BannerTagsController }> = ({ controller }) => {
  const { tags, filterIds, setFilterIds } = controller;
  const [expanded, setExpanded] = useState(false);

  if (!controller.enabled) return null;

  const collapsible = tags.length > VISIBLE_LIMIT;
  // たたんでいても、絞り込みに使っているタグは見えるところに残す
  const visibleTags =
    collapsible && !expanded
      ? tags.filter((tag, index) => index < VISIBLE_LIMIT || filterIds.includes(tag.id))
      : tags;
  const hiddenCount = tags.length - visibleTags.length;

  const toggle = (tagId: number) =>
    setFilterIds(filterIds.includes(tagId) ? filterIds.filter((id) => id !== tagId) : [...filterIds, tagId]);

  return (
    <div role="group" aria-label="タグで絞り込む" className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 inline-flex items-center gap-1 text-sm font-medium text-gray-600">
        <Tag className="h-4 w-4" aria-hidden />
        タグ
      </span>
      {visibleTags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          selected={filterIds.includes(tag.id)}
          onToggle={() => toggle(tag.id)}
          onUpdate={(changes) => void controller.updateTag(tag, changes)}
          onDelete={() => void controller.deleteTag(tag)}
        />
      ))}
      {hiddenCount > 0 && (
        <TaskyButton type="button" variant="ghost" size="sm" onClick={() => setExpanded(true)} className="h-8 px-2 font-normal">
          ほか {hiddenCount} 件
        </TaskyButton>
      )}
      {collapsible && expanded && (
        <TaskyButton type="button" variant="ghost" size="sm" onClick={() => setExpanded(false)} className="h-8 px-2 font-normal">
          たたむ
        </TaskyButton>
      )}
      <NewTagButton onCreate={(name) => void controller.createTag(name)} />
    </div>
  );
};

export default ImageTagBar;
